import axios from 'axios';
import { getAuthHeader } from './authService';

const API_BASE_URL = '/api';

export interface UserDTO {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  userType: string;
  universityId?: number;
  schoolId?: string;
  status: string;
  createdAt: string;
  assignedProviderId?: number;
  assignedProviderName?: string;
  program?: string;
  department?: string;
  studentCount?: number;
  lastLogin?: string;
}

export interface CreateUserRequest {
  // Basic Information
  userType: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  
  // Contact Information
  phoneCountryCode?: string;
  phoneNumber?: string;
  address?: string;
  
  // Personal Information
  gender?: string;
  dateOfBirth?: Date | string;
  race?: string;
  
  // Academic/Professional Information
  universityId?: number;
  schoolId?: string;
  yearInSchool?: string;
  identityType?: string;
  program?: string;
  department?: string;
  assignedProviderId?: number;
  
  // Emergency Contact
  emergencyContactName?: string;
  emergencyContactRelation?: string;
  emergencyContactPhone?: string;
}

export interface UpdateUserRequest {
  userId: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  status?: string;
  program?: string;
  department?: string;
  assignedProviderId?: number;
}

const userService = {
  // Fetch users by type
  getStudents: async (): Promise<UserDTO[]> => {
    const response = await axios.get(`${API_BASE_URL}/users/students`, { headers: getAuthHeader() });
    return response.data;
  },

  getProviders: async (): Promise<UserDTO[]> => {
    const response = await axios.get(`${API_BASE_URL}/users/providers`, { headers: getAuthHeader() });
    return response.data;
  },

  getAdmins: async (): Promise<UserDTO[]> => {
    const response = await axios.get(`${API_BASE_URL}/users/admins`, { headers: getAuthHeader() });
    return response.data;
  },

  // Get single user
  getUser: async (userId: number): Promise<UserDTO> => {
    const response = await axios.get(`${API_BASE_URL}/users/${userId}`, { headers: getAuthHeader() });
    return response.data;
  },

  // Create new user
  createUser: async (request: CreateUserRequest): Promise<UserDTO> => {
    const response = await axios.post(`${API_BASE_URL}/users`, request, { headers: getAuthHeader() });
    return response.data;
  },

  // Update user
  updateUser: async (request: UpdateUserRequest): Promise<UserDTO> => {
    const { userId, ...updateData } = request;
    const response = await axios.put(`${API_BASE_URL}/users/${userId}`, updateData, { headers: getAuthHeader() });
    return response.data;
  },

  // Deactivate/activate user
  updateUserStatus: async (userId: number, status: string): Promise<void> => {
    await axios.patch(`${API_BASE_URL}/users/${userId}/status`, { status }, { headers: getAuthHeader() });
  },

  // Delete user (admin only)
  deleteUser: async (userId: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/users/${userId}`, { headers: getAuthHeader() });
  },

  // Get universities for dropdown
  getUniversities: async () => {
    const response = await axios.get(`${API_BASE_URL}/universities`, { headers: getAuthHeader() });
    return response.data;
  },

  // Get departments
  getDepartments: async () => {
    const response = await axios.get(`${API_BASE_URL}/departments`, { headers: getAuthHeader() });
    return response.data;
  },

  // Get programs
  getPrograms: async () => {
    // For now, return static list - could be made dynamic later
    return [
      'Psychology',
      'Social Work',
      'Counseling',
      'Mental Health',
      'Education',
      'Nursing',
      'Medicine',
      'Public Health'
    ];
  },

  // Assign student to provider
  assignStudentToProvider: async (studentId: number, providerId: number): Promise<void> => {
    await axios.post(`${API_BASE_URL}/users/students/${studentId}/assign-provider`, { providerId }, { headers: getAuthHeader() });
  },

  // Get students assigned to a provider
  getProviderStudents: async (providerId: number): Promise<UserDTO[]> => {
    const response = await axios.get(`${API_BASE_URL}/users/providers/${providerId}/students`, { headers: getAuthHeader() });
    return response.data;
  },

  // Get form options for user creation
  getFormOptions: async () => {
    const response = await axios.get(`${API_BASE_URL}/form-options/user-creation`, { headers: getAuthHeader() });
    return response.data;
  },

  // Get providers by university
  getProvidersByUniversity: async (universityId: number): Promise<UserDTO[]> => {
    const response = await axios.get(`${API_BASE_URL}/universities/${universityId}/providers`, { headers: getAuthHeader() });
    return response.data;
  },

  // Get departments by university
  getDepartmentsByUniversity: async (universityId: number) => {
    const response = await axios.get(`${API_BASE_URL}/universities/${universityId}/departments`, { headers: getAuthHeader() });
    return response.data;
  }
};

export default userService;